import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { ThemeToggle, LangToggle } from "@/components/Toggles";
import { CURRENCY_SYMBOL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Droplets, Wine, ShoppingBag, MapPin, Phone, Minus, Plus, Loader2, Leaf } from "lucide-react";
import { PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";

export default function Storefront() {
  const { slug } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get(`/public/store/${slug}`).then((r) => setData(r.data)).catch(() => setError(true)); }, [slug]);

  if (error) return <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">{t("order_not_found")}</div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const HERO = "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

  return (
    <div className="min-h-screen bg-background grain-bg">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 px-4 py-4 backdrop-blur-md sm:px-6">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Droplets className="h-5 w-5" /></div>
            <span className="font-heading text-lg font-bold">{data.mill_name}</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="track-order-link" onClick={() => window.open("/order-status", "_blank")} className="text-sm font-medium text-muted-foreground hover:text-foreground">{t("track_order")}</button>
            <LangToggle /><ThemeToggle />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src={HERO} alt="Olive oil" className="h-72 w-full object-cover sm:h-96" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20" />
        <div className="absolute inset-0 mx-auto flex max-w-6xl flex-col justify-end px-4 pb-8 sm:px-6">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-amber/90 px-3 py-1 text-xs font-semibold text-olive"><Leaf className="h-3.5 w-3.5" /> {data.mill_name}</span>
          <h1 className="mt-3 font-heading text-3xl font-extrabold text-white sm:text-5xl">{t("browse_products")}</h1>
          <p className="mt-2 max-w-lg text-white/80">{t("storefront_sub")}</p>
          {data.address && <p className="mt-3 flex items-center gap-1.5 text-sm text-white/70"><MapPin className="h-4 w-4" /> {data.address}</p>}
        </div>
      </section>

      {/* Products */}
      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {data.products.map((p) => (
            <div key={p.id} data-testid={`store-product-${p.id}`} className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-all hover:-translate-y-1 hover:shadow-lg">
              {p.image ? (
                <img src={p.image} alt={p.name} data-testid={`store-image-${p.id}`} className="h-48 w-full object-cover" />
              ) : (
                <div className="flex h-48 w-full items-center justify-center bg-amber-glow text-amber-deep"><Wine className="h-12 w-12" /></div>
              )}
              <div className="flex flex-1 flex-col p-6">
              <div className="flex items-center justify-end">
                <span className="rounded-full bg-olive-light px-2.5 py-1 text-xs font-semibold text-olive">{t(`variety_${p.variety}`)}</span>
              </div>
              <h3 className="mt-4 font-heading text-lg font-bold">{p.name}</h3>
              <p className="text-sm text-muted-foreground">{p.bottle_size}</p>
              {p.description && <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{p.description}</p>}
              <div className="mt-4 flex items-center justify-between">
                <span className="font-heading text-2xl font-extrabold">{CURRENCY_SYMBOL[p.currency]}{p.price}</span>
                <span className={`text-xs ${p.stock > 0 ? "text-green-600" : "text-destructive"}`}>{p.stock > 0 ? `${p.stock} ${t("in_stock")}` : t("out_of_stock")}</span>
              </div>
              <Button data-testid={`order-btn-${p.id}`} onClick={() => setSelected(p)} disabled={p.stock <= 0} className="mt-4 w-full rounded-full bg-primary hover:bg-olive-hover">
                <ShoppingBag className="mr-1.5 h-4 w-4" /> {t("order_now")}
              </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {selected && <OrderDialog slug={slug} product={selected} onClose={() => setSelected(null)} t={t} />}
    </div>
  );
}

function OrderDialog({ slug, product, onClose, t }) {
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_email: "", quantity: 1, note: "" });
  const [busy, setBusy] = useState(false);
  const [promo, setPromo] = useState("");
  const [applied, setApplied] = useState(null);
  const qty = parseInt(form.quantity) || 1;
  const base = Number(product.price) * qty;
  const discountVal = applied ? (applied.type === "percent" ? base * (applied.value / 100) : Math.min(base, applied.value)) : 0;
  const total = Math.max(0, base - discountVal).toFixed(2);
  const canPay = product.currency === "EUR";

  const applyPromo = async () => {
    try {
      const { data } = await api.post(`/public/store/${slug}/validate-code`, { code: promo });
      setApplied(data); toast.success(t("code_applied"));
    } catch (e) { setApplied(null); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/public/store/${slug}/order`, { product_id: product.id, ...form, quantity: qty });
      toast.success(t("order_request_sent"));
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const payHandlers = {
    createOrder: () => {
      if (!form.customer_name || !form.customer_phone) {
        toast.error(`${t("your_name")} / ${t("your_phone")}`);
        return Promise.reject(new Error("missing-fields"));
      }
      return api.post(`/public/store/${slug}/checkout/create-order`, {
        product_id: product.id, quantity: qty, customer_name: form.customer_name,
        customer_phone: form.customer_phone, customer_email: form.customer_email, note: form.note,
        discount_code: applied ? applied.code : "",
      }).then((r) => r.data.order_id);
    },
    onApprove: (data) => api.post(`/public/store/${slug}/checkout/capture/${data.orderID}`).then(() => {
      toast.success(t("purchase_complete"));
      onClose();
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || t("payment_failed"))),
    onError: () => toast.error(t("payment_failed")),
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="order-dialog">
        <DialogHeader><DialogTitle>{t("request_order")} · {product.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>{t("your_name")}</Label><Input data-testid="order-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>{t("your_phone")}</Label><Input data-testid="order-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{t("order_qty")}</Label>
              <div className="flex items-center gap-1">
                <Button type="button" size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={() => setForm((f) => ({ ...f, quantity: Math.max(1, (parseInt(f.quantity) || 1) - 1) }))}><Minus className="h-4 w-4" /></Button>
                <Input data-testid="order-qty" type="number" className="text-center" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                <Button type="button" size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={() => setForm((f) => ({ ...f, quantity: (parseInt(f.quantity) || 1) + 1 }))}><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
          <div className="space-y-1.5"><Label>{t("your_email")} ({t("optional")})</Label><Input data-testid="order-email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>{t("order_note")}</Label><Textarea data-testid="order-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Input data-testid="promo-input" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder={t("promo_code")} className="h-9" />
          <Button data-testid="apply-promo-btn" size="sm" variant="outline" onClick={applyPromo} disabled={!promo} className="shrink-0">{t("apply_code")}</Button>
        </div>
        <div className="mt-2 space-y-1 rounded-lg bg-olive-light px-4 py-2.5">
          {applied && (
            <div className="flex items-center justify-between text-xs text-olive"><span>{t("discount")} ({applied.code})</span><span>-€{discountVal.toFixed(2)}</span></div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-olive">{t("total_label")}</span>
            <span data-testid="checkout-total" className="font-heading text-lg font-bold text-olive">€{total}</span>
          </div>
        </div>
        {canPay ? (
          <div className="space-y-2 pt-1">
            <PayPalButtons key={`${product.id}-${qty}`} forceReRender={[qty, product.id]} style={{ layout: "horizontal", height: 45, shape: "pill", label: "pay", tagline: false }} fundingSource={undefined} {...payHandlers} />
            <button data-testid="request-instead-btn" onClick={submit} disabled={busy || !form.customer_name || !form.customer_phone} className="w-full text-center text-xs text-muted-foreground underline disabled:opacity-40">{t("request_instead")}</button>
          </div>
        ) : (
          <DialogFooter className="pt-2">
            <p className="mr-auto self-center text-xs text-muted-foreground">{t("online_eur_only")}</p>
            <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
            <Button data-testid="submit-order-btn" onClick={submit} disabled={busy || !form.customer_name || !form.customer_phone} className="bg-primary hover:bg-olive-hover">{busy ? t("loading") : t("submit_request")}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
